import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { API, api } from "@/lib/api";
import { Microscope, Lock, KeyRound, Eye } from "lucide-react";

const BASIS_COLORS = {
  EXPLICIT_TEXT: "bg-emerald-100 text-emerald-800",
  VISIBLE_GEOMETRY: "bg-sky-100 text-sky-800",
  CROSS_VIEW_INFERENCE: "bg-amber-100 text-amber-800",
  PROCESS_INFERENCE: "bg-rose-100 text-rose-800",
};

const CAT_LABELS = {
  title_block: "Cartiglio", material: "Materiale", material_quality: "Qualità materiale",
  thickness: "Spessore", finish: "Finitura", quantity: "Quantità", units_scale: "Unità e scala",
  projection_method: "Proiezione", views: "Viste", components: "Componenti",
  general_dimensions: "Dimensioni generali", holes: "Fori", slots: "Asole", bends: "Pieghe",
  radii: "Raggi", roll_bending: "Calandrature", welds: "Saldature", assemblies: "Assemblaggi",
  technical_notes: "Note tecniche", tolerances: "Tolleranze",
  explicit_operations: "Lavorazioni esplicite", inferred_operations: "Lavorazioni dedotte",
  uncertain_illegible: "Incerto / illeggibile",
};

const ClaimRow = ({ c }) => (
  <tr className="border-b border-zinc-100 align-top" data-testid={`claim-${c.id}`}>
    <td className="py-1 pr-2 font-mono text-[10px]">{c.id}</td>
    <td className="py-1 pr-2 text-[11px]">{CAT_LABELS[c.category] || c.category}</td>
    <td className="py-1 pr-2 text-[11px]">{c.statement}{c.corrected_by_zoom && <Badge className="ml-1 bg-violet-100 text-violet-800 text-[9px]">corretto da zoom</Badge>}{c.added_by_zoom && <Badge className="ml-1 bg-violet-100 text-violet-800 text-[9px]">aggiunto da zoom</Badge>}</td>
    <td className="py-1 pr-2 text-[11px] font-medium">{c.value ?? "—"}{c.unit ? ` ${c.unit}` : ""}</td>
    <td className="py-1 pr-2 text-[10px] italic">{c.visible_text ?? "—"}</td>
    <td className="py-1 pr-2 text-[10px]">{c.view ?? "—"}<div className="text-zinc-400">{c.zone}</div></td>
    <td className="py-1 pr-2"><Badge className={`text-[9px] ${BASIS_COLORS[c.basis] || ""}`}>{c.basis}</Badge></td>
    <td className="py-1 pr-2 text-[10px]">{c.certainty}{c.needs_zoom && " 🔍"}</td>
    <td className="py-1 text-[10px]">{(c.alternatives || []).join("; ") || "—"}</td>
  </tr>
);

const ClaimsTable = ({ claims, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-left">
      <thead><tr className="text-[10px] uppercase text-zinc-500 border-b">
        <th className="py-1 pr-2">ID</th><th className="py-1 pr-2">Categoria</th><th className="py-1 pr-2">Affermazione</th>
        <th className="py-1 pr-2">Valore</th><th className="py-1 pr-2">Testo visibile</th><th className="py-1 pr-2">Vista/Zona</th>
        <th className="py-1 pr-2">Base</th><th className="py-1 pr-2">Certezza</th><th className="py-1">Alternative</th>
      </tr></thead>
      <tbody>{(claims || []).map((c) => <ClaimRow key={c.id} c={c} />)}</tbody>
    </table>
  </div>
);

export default function SpikeP1() {
  const [st, setSt] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [payloadName, setPayloadName] = useState(null);
  const [payloadBody, setPayloadBody] = useState(null);
  const [payloadFiles, setPayloadFiles] = useState([]);
  const timer = useRef(null);

  const load = useCallback(() => {
    api.get("/spike-p1/status").then((r) => setSt(r.data)).catch(() => toast.error("Errore stato spike"));
    api.get("/spike-p1/comparison").then((r) => setCmp(r.data)).catch(() => {});
    api.get("/spike-p1/payloads").then((r) => setPayloadFiles(r.data.files || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); return () => timer.current && clearInterval(timer.current); }, [load]);

  useEffect(() => {
    if (st?.running && !timer.current) {
      timer.current = setInterval(async () => {
        const r = await api.get("/spike-p1/status");
        setSt(r.data);
        if (!r.data.running) { clearInterval(timer.current); timer.current = null; load(); }
      }, 4000);
    }
  }, [st, load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); okMsg && toast.success(okMsg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };

  const saveKey = () => act(async () => {
    await api.post("/spike-p1/secret", { api_key: keyInput });
    setKeyInput("");
  }, "Secret salvato (mai mostrato né loggato)");

  const openPayload = async (name) => {
    setPayloadName(name);
    const r = await api.get(`/spike-p1/payloads/${name}`);
    setPayloadBody(JSON.stringify(r.data, null, 1).slice(0, 40000));
  };

  if (!st) return <div className="p-4 text-sm" data-testid="spike-loading">Caricamento spike P1…</div>;

  const b = st.budget || {};
  const finals = cmp?.final_claims || [];
  const claims = st.first_read_done ? (cmp?.first_read?.claims || []) : [];

  return (
    <div className="space-y-4 p-1" data-testid="spike-p1-page">
      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Microscope size={15} /> P1 — AI-FIRST READER SPIKE ({st.model} · reasoning max · PDF high · crop original)
          <Badge className="bg-zinc-200 text-zinc-700 text-[10px]">P0.5 ARCHIVED_FAILED_BASELINE</Badge>
        </CardTitle></CardHeader>
        <CardContent className="space-y-2 text-xs">
          <div className="flex gap-2 flex-wrap items-center" data-testid="spike-budget">
            <Badge className="bg-zinc-100 text-zinc-700">Chiamate {b.calls_made}/{b.max_calls}</Badge>
            <Badge className="bg-zinc-100 text-zinc-700">Costo ${Number(b.spent_usd || 0).toFixed(4)}/${b.max_cost_usd}</Badge>
            <Badge className="bg-zinc-100 text-zinc-700">Zoom {b.zooms_done}/{b.max_zooms}</Badge>
            <Badge className={st.openai_secret_present ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"} data-testid="spike-secret-badge">
              openai_secret_present: {String(st.openai_secret_present)}
            </Badge>
            {st.frozen && <Badge className="bg-blue-100 text-blue-800">OUTPUT CONGELATO</Badge>}
            {st.gt_opened && <Badge className="bg-purple-100 text-purple-800">GT APERTA</Badge>}
            {st.running && <Badge className="bg-amber-100 text-amber-800 animate-pulse">In corso: {st.running.kind}…</Badge>}
            {st.aborted_reason && <Badge className="bg-rose-100 text-rose-800">INTERROTTO: {st.aborted_reason}</Badge>}
          </div>
          {st.visual_verification && <div className="text-[11px] text-zinc-500">{st.visual_verification}</div>}
          <div className="flex gap-2 flex-wrap">
            {!st.prepared && <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => act(() => api.post("/spike-p1/prepare"), "Copia neutra creata")} data-testid="spike-prepare-btn">1. Prepara copia neutra</Button>}
            {st.prepared && !st.first_read_done && <Button size="sm" className="h-7 text-xs" disabled={busy || !st.openai_secret_present || !!st.running} onClick={() => act(() => api.post("/spike-p1/run/global"), "Lettura globale avviata (1 chiamata)")} data-testid="spike-global-btn">2. Lettura globale (1 chiamata)</Button>}
            {st.first_read_done && !st.frozen && <Button size="sm" variant="secondary" className="h-7 text-xs" disabled={busy || !!st.running} onClick={() => act(() => api.post("/spike-p1/freeze"), "Output AI congelato")} data-testid="spike-freeze-btn">4. Congela output AI</Button>}
            {st.frozen && !st.gt_opened && <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={busy} onClick={() => act(() => api.post("/spike-p1/open-gt"), "Ground truth aperta (solo confronto)")} data-testid="spike-open-gt-btn">5. Apri ground truth</Button>}
          </div>
        </CardContent>
      </Card>

      {!st.openai_secret_present && (
        <Card data-testid="spike-secret-card" className="border-amber-300">
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><KeyRound size={14} /> OPENAI_API_KEY — inserimento protetto (solo Owner)</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs">
            <p className="text-zinc-600">La chiave viaggia su HTTPS verso il tuo backend, viene salvata in un file locale con permessi 0600 <b>escluso da git</b>, non è mai loggata, mai archiviata nei payload e mai restituita dalle API. Viene registrato solo <code>openai_secret_present: true</code>.</p>
            <div className="flex gap-2 max-w-md">
              <Input type="password" placeholder="sk-…" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && keyInput.length >= 21) saveKey(); }} className="h-8 text-xs" data-testid="spike-secret-input" autoComplete="off" />
              <Button size="sm" className="h-8 text-xs" disabled={busy || keyInput.length < 21} onClick={saveKey} data-testid="spike-secret-save-btn"><Lock size={12} className="mr-1" />Salva secret</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {st.prepared && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">PDF completo — {st.neutral_id}</CardTitle></CardHeader>
          <CardContent>
            <div className="relative inline-block max-w-full" data-testid="spike-pdf-view">
              <img src={`${API}/spike-p1/page-image`} alt="disegno" className="max-w-full border border-zinc-200" crossOrigin="use-credentials" />
              {finals.filter((c) => c.bbox_pct).map((c) => (
                <div key={c.id} title={`${c.id}: ${c.statement}`}
                  className={`absolute border-2 ${c.basis === "EXPLICIT_TEXT" ? "border-emerald-500" : c.basis === "VISIBLE_GEOMETRY" ? "border-sky-500" : "border-amber-500"}`}
                  style={{ left: `${c.bbox_pct.x0}%`, top: `${c.bbox_pct.y0}%`, width: `${c.bbox_pct.x1 - c.bbox_pct.x0}%`, height: `${c.bbox_pct.y1 - c.bbox_pct.y0}%` }} />
              ))}
            </div>
            <div className="flex gap-2 mt-1 text-[10px] text-zinc-500">
              <span className="text-emerald-600">■ EXPLICIT_TEXT</span><span className="text-sky-600">■ VISIBLE_GEOMETRY</span><span className="text-amber-600">■ inferenze</span>
            </div>
          </CardContent>
        </Card>
      )}

      {st.first_read_done && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Prima lettura globale ({claims.length} claims)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {cmp?.first_read?.reading_summary && <p className="text-[11px] text-zinc-600 italic">{cmp.first_read.reading_summary}</p>}
            <ClaimsTable claims={claims} testId="spike-first-read-table" />
          </CardContent>
        </Card>
      )}

      {st.first_read_done && (st.zoom_requests || []).length > 0 && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2"><Eye size={14} /> 3. Ciclo di zoom richiesti dal modello ({st.zoom_requests.length}, max 6)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {st.zoom_requests.map((z) => (
              <div key={z.zone_id} className="border border-zinc-200 rounded p-2 text-xs" data-testid={`spike-zoom-${z.zone_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-zinc-100 text-zinc-700 text-[10px]">{z.zone_id}</Badge>
                  <span>{z.reason}</span>
                  <span className="text-zinc-400">strumenti: {(z.tools || []).join(", ") || "nessuno"}</span>
                  {z.status === "done" ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">fatto</Badge> :
                    !st.frozen && <Button size="sm" className="h-6 text-[10px]" disabled={busy || !!st.running} onClick={() => act(() => api.post(`/spike-p1/run/zoom/${z.zone_id}`), `Zoom ${z.zone_id} avviato`)} data-testid={`spike-zoom-run-${z.zone_id}`}>Esegui zoom (1 chiamata)</Button>}
                </div>
                {z.bbox_pct && <img src={`${API}/spike-p1/crop-image?x0=${z.bbox_pct.x0}&y0=${z.bbox_pct.y0}&x1=${z.bbox_pct.x1}&y1=${z.bbox_pct.y1}`} alt={z.zone_id} className="mt-1 max-h-48 border border-zinc-100" crossOrigin="use-credentials" />}
              </div>
            ))}
            {(cmp?.zone_reviews || []).map((zr, i) => (
              <div key={i} className="border-l-2 border-violet-300 pl-2 text-[11px]" data-testid={`spike-zone-review-${zr.zone_id}`}>
                <b>{zr.zone_id}</b> — conferme: {(zr.confirmations || []).map((c) => c.claim_id).join(", ") || "—"} ·
                correzioni: {(zr.corrections || []).map((c) => `${c.claim_id}→${c.corrected_value}`).join(", ") || "—"} ·
                ritiri: {(zr.withdrawals || []).map((w) => w.claim_id).join(", ") || "—"} ·
                nuovi: {(zr.new_claims || []).map((n) => n.id).join(", ") || "—"}
                <div className="text-zinc-500">{zr.zone_note}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {st.frozen && finals.length > 0 && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Risultato congelato dopo gli zoom ({finals.length} claims) — sha256 {cmp?.state?.frozen_sha256?.slice(0, 12)}…</CardTitle></CardHeader>
          <CardContent><ClaimsTable claims={finals} testId="spike-final-table" /></CardContent>
        </Card>
      )}

      {st.gt_opened && cmp?.gt_comparison && (
        <Card className="border-purple-300">
          <CardHeader className="py-3"><CardTitle className="text-sm">Confronto con ground truth (aperta SOLO dopo il congelamento)</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto" data-testid="spike-gt-comparison">
            <table className="w-full text-left text-[11px]">
              <thead><tr className="text-[10px] uppercase text-zinc-500 border-b">
                <th className="py-1 pr-2">Campo</th><th className="py-1 pr-2">Ground truth (FASE A)</th><th className="py-1">Affermazioni AI congelate</th>
              </tr></thead>
              <tbody>
                {cmp.gt_comparison.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-100 align-top">
                    <td className="py-1 pr-2 font-medium">{r.label}</td>
                    <td className="py-1 pr-2">{r.gt_value ?? "—"}{r.gt_count != null ? ` (n=${r.gt_count})` : ""}{r.gt_state && <Badge className="ml-1 bg-zinc-100 text-zinc-600 text-[9px]">{r.gt_state}</Badge>}{r.gt_note && <div className="text-zinc-400 text-[10px]">{r.gt_note}</div>}</td>
                    <td className="py-1">{(r.ai_claims || []).length === 0 ? <span className="text-rose-600">non dichiarato dall'AI</span> :
                      r.ai_claims.map((c) => <div key={c.id}><span className="font-mono text-[9px]">{c.id}</span> {c.value ?? c.statement}{c.unit ? ` ${c.unit}` : ""} <Badge className={`text-[9px] ${BASIS_COLORS[c.basis] || ""}`}>{c.basis}</Badge> {c.certainty}</div>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(st.calls || []).length > 0 && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Chiamate, token, costo e latenza</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-[11px]" data-testid="spike-calls-table">
              <thead><tr className="text-[10px] uppercase text-zinc-500 border-b">
                <th className="py-1 pr-2">Call</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2">Stato</th><th className="py-1 pr-2">Model restituito</th>
                <th className="py-1 pr-2">In</th><th className="py-1 pr-2">Out</th><th className="py-1 pr-2">Reasoning</th><th className="py-1 pr-2">Costo $</th><th className="py-1 pr-2">Latenza s</th><th className="py-1">Errore</th>
              </tr></thead>
              <tbody>{st.calls.map((c) => (
                <tr key={c.call_id} className="border-b border-zinc-100">
                  <td className="py-1 pr-2 font-mono">{c.call_id}</td><td className="py-1 pr-2">{c.kind}</td>
                  <td className="py-1 pr-2">{c.status}</td><td className="py-1 pr-2">{c.model_returned ?? "—"}</td>
                  <td className="py-1 pr-2">{c.input_tokens ?? "—"}</td><td className="py-1 pr-2">{c.output_tokens ?? "—"}</td>
                  <td className="py-1 pr-2">{c.reasoning_tokens ?? "—"}</td><td className="py-1 pr-2">{c.cost_usd != null ? c.cost_usd.toFixed(4) : "—"}</td>
                  <td className="py-1 pr-2">{c.latency_s ?? "—"}</td><td className="py-1 text-rose-600">{c.error ?? ""}</td>
                </tr>))}</tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {payloadFiles.length > 0 && (
        <Card>
          <CardHeader className="py-3"><CardTitle className="text-sm">Payload e risposte grezze (senza header/API key)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex gap-1 flex-wrap">
              {payloadFiles.map((f) => (
                <Button key={f} size="sm" variant={payloadName === f ? "default" : "outline"} className="h-6 text-[10px]" onClick={() => openPayload(f)} data-testid={`spike-payload-${f}`}>{f}</Button>
              ))}
            </div>
            {payloadBody && <pre className="text-[9px] bg-zinc-50 border border-zinc-200 rounded p-2 max-h-80 overflow-auto" data-testid="spike-payload-body">{payloadBody}</pre>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
